import { createApi } from '@reduxjs/toolkit/query/react'
import { API_END_POINTS } from './apiEndpoints'
import { DynamicBaseQueryHeader } from './DynamicBaseQueryHeader'

export type LogFileItem = {
  fileName: string
  createdOn: string
  modifiedOn: string
  sizeBytes: number
  isLocked?: boolean
  status?: string | null
}

export type LogFolderInfo = {
  serviceName: string
  logFolderRelativePath: string
  logFolderPath: string
  machineName: string
  processUserName: string
  ftpHint: string
}

export type LogFilesListResult = LogFolderInfo & {
  files: LogFileItem[]
}

const buildLogFileEndpoints = (builder: any) => ({
  getAllLogFiles: builder.query({
    query: ({ startDate, endDate }: { startDate?: string; endDate?: string }) => {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate)
      if (endDate) params.set('endDate', endDate)
      const qs = params.toString()
      return {
        url: `${API_END_POINTS.getAllLogFiles}${qs ? `?${qs}` : ''}`,
        method: 'GET',
      }
    },
    providesTags: ['LogFiles'],
  }),
  getLogFolderInfo: builder.query({
    query: () => ({
      url: API_END_POINTS.getLogFolderInfo,
      method: 'GET',
    }),
  }),
  cloneTodayLogFile: builder.mutation({
    query: () => ({
      url: API_END_POINTS.cloneTodayLogFile,
      method: 'PUT',
    }),
    invalidatesTags: ['LogFiles'],
  }),
  cloneLogFileByName: builder.mutation({
    query: (sourceFile: string) => ({
      url: `${API_END_POINTS.cloneLogFileByName}?sourceFile=${encodeURIComponent(sourceFile)}`,
      method: 'GET',
    }),
    invalidatesTags: ['LogFiles'],
  }),
  downloadLogFileByName: builder.query({
    query: (fileName: string) => ({
      url: `${API_END_POINTS.downloadLogFileByName}?fileName=${encodeURIComponent(fileName)}`,
      method: 'GET',
      responseHandler: async (response: Response) => response.blob(),
    }),
  }),
  downloadLogFilesZip: builder.mutation({
    query: (fileNames: string[]) => ({
      url: API_END_POINTS.downloadLogFilesZip,
      method: 'POST',
      body: fileNames,
      responseHandler: async (response: Response) => response.blob(),
    }),
  }),
})

/** SS API LogFile endpoints (baseURL) */
export const LogFileApi = createApi({
  reducerPath: 'LogFileApi',
  baseQuery: DynamicBaseQueryHeader('LogFile'),
  tagTypes: ['LogFiles'],
  endpoints: (builder) => buildLogFileEndpoints(builder),
})

/** SS Micro Service LogFile endpoints (microServiceURL) */
export const MicroLogFileApi = createApi({
  reducerPath: 'MicroLogFileApi',
  baseQuery: DynamicBaseQueryHeader('LogFile', 'microServiceURL'),
  tagTypes: ['LogFiles'],
  endpoints: (builder) => buildLogFileEndpoints(builder),
})

export const {
  useGetAllLogFilesQuery,
  useLazyGetAllLogFilesQuery,
  useCloneTodayLogFileMutation,
  useCloneLogFileByNameMutation,
  useLazyDownloadLogFileByNameQuery,
  useDownloadLogFilesZipMutation,
} = LogFileApi

export const {
  useGetAllLogFilesQuery: useGetAllMicroLogFilesQuery,
  useLazyGetAllLogFilesQuery: useLazyGetAllMicroLogFilesQuery,
  useCloneTodayLogFileMutation: useCloneTodayMicroLogFileMutation,
  useCloneLogFileByNameMutation: useCloneMicroLogFileByNameMutation,
  useLazyDownloadLogFileByNameQuery: useLazyDownloadMicroLogFileByNameQuery,
  useDownloadLogFilesZipMutation: useDownloadMicroLogFilesZipMutation,
} = MicroLogFileApi
